import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { authApi, authorizationApi, organizationApi } from '@/lib/pillarApi';
import {
    type ActiveRoleAssignmentResponse,
    type GetOrganizationResponse,
} from '@pillar-portal/api-types';
import { getOrganizationCapacityLabel, organizationHasCapacity } from '@/lib/organization-capacity';
import { runInBackground } from '@/lib/utils';
import { merchantSchema, type MerchantFormData } from '@/lib/validation-organization';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { AuthorizableAction, ResourceType } from '@pillar-portal/database/dist/frontend';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type { OrganizationOmnisearchListItem } from '@pillar-portal/api-types';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

function CreateMerchantContent() {
    const log = createComponentLogger('CreateMerchantPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const bumpOrganizationListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpOrganizationListsVersion
    );

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            enabled: !!user,
            staleTime: 30000,
        });
    const canManageOrganizations: boolean = hasInternalAdminPrivileges(
        getActiveRoleAssignmentOperationalResponse(activeRoleAssignment)
    );

    const parentIsoUuidFromUrl = searchParams.get('parent_iso_uuid');
    const isParentIsoLocked = !!parentIsoUuidFromUrl;

    const parentMerchantUuidFromUrl = searchParams.get('parent_merchant_uuid');
    const isParentMerchantLocked = !!parentMerchantUuidFromUrl;
    const authorizationTargetOrganizationUuid: string | null =
        parentMerchantUuidFromUrl ?? parentIsoUuidFromUrl;
    const { data: canCreateMerchant = false, isLoading: isCreateAuthorizationLoading } =
        useQuery<boolean>({
            queryKey: ['merchantCreateAuthorization', authorizationTargetOrganizationUuid],
            queryFn: async () => {
                if (canManageOrganizations) {
                    return true;
                }
                if (authorizationTargetOrganizationUuid === null) {
                    return false;
                }
                const response = await authorizationApi.isAuthorized({
                    action: AuthorizableAction.CREATE,
                    resource_type: ResourceType.ORGANIZATION,
                    target_resource: {
                        designator_type: ResourceType.ORGANIZATION,
                        organization_uuid: authorizationTargetOrganizationUuid,
                        descendants: false,
                    },
                });
                return response.is_authorized;
            },
            enabled: !isActiveRoleAssignmentLoading,
        });
    const { data: lockedParentMerchant } = useQuery<GetOrganizationResponse | null>({
        queryKey: ['organization', parentMerchantUuidFromUrl],
        queryFn: async () => {
            if (parentMerchantUuidFromUrl === null) {
                return null;
            }
            return await organizationApi.getOrganization(parentMerchantUuidFromUrl);
        },
        enabled: parentMerchantUuidFromUrl !== null,
        staleTime: 30000,
    });

    const { data: lockedParentIso } = useQuery<GetOrganizationResponse | null>({
        queryKey: ['organization', parentIsoUuidFromUrl],
        queryFn: async () => {
            if (parentIsoUuidFromUrl === null) {
                return null;
            }
            return await organizationApi.getOrganization(parentIsoUuidFromUrl);
        },
        enabled: parentIsoUuidFromUrl !== null,
        staleTime: 30000,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<MerchantFormData>({
        resolver: zodResolver(merchantSchema),
    });

    const createMerchantMutation = useMutation({
        mutationFn: organizationApi.createMerchant,
        onSuccess: (data) => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizationListing'], exact: false }),
                (err) => {
                    log.error('Error invalidating organization listing queries:', err);
                }
            );
            // New org can be hidden by the accessible-org filter if this query is cached.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: ['authorization', 'accessible_organization_ids'],
                }),
                (err) => {
                    log.error('Error invalidating accessible org ids query:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/organizations/${data.organization_uuid}?view=merchant`,
                    title: 'Organization Details',
                },
            });
        },
    });

    const onSubmit = (data: MerchantFormData) => {
        if (!canCreateMerchant) {
            throw new Error('Access denied: insufficient permissions to create merchant');
        }
        const effectiveParentMerchantUuid: string | undefined =
            parentMerchantUuidFromUrl ?? data.parent_organization_uuid;
        const effectiveIsoOrganizationUuid: string | undefined =
            parentIsoUuidFromUrl ?? data.iso_organization_uuid;

        createMerchantMutation.mutate({
            name: data.name,
            ...(effectiveParentMerchantUuid && {
                parent_organization_uuid: effectiveParentMerchantUuid,
            }),
            ...(effectiveIsoOrganizationUuid && {
                iso_organization_uuid: effectiveIsoOrganizationUuid,
            }),
        });
    };

    const isLockedToParentMerchant = isParentMerchantLocked;
    const isLockedToParentIso = isParentIsoLocked;
    const lockedParentMerchantName: string | null = lockedParentMerchant?.name ?? null;
    const lockedParentIsoName: string | null = lockedParentIso?.name ?? null;

    const pageTitle = isLockedToParentMerchant
        ? lockedParentMerchantName !== null
            ? `Create Subsidiary Merchant of ${lockedParentMerchantName}`
            : 'Create Subsidiary Merchant'
        : isLockedToParentIso
          ? lockedParentIsoName !== null
              ? `Create Merchant for ${lockedParentIsoName}`
              : 'Create Merchant'
          : 'Create Merchant';

    if (isActiveRoleAssignmentLoading || isCreateAuthorizationLoading) {
        return <div>Loading...</div>;
    }
    if (!canCreateMerchant) {
        return (
            <div>
                <BackButton href="/dashboard" tooltip="Back to Dashboard" />
                <div className="text-red-600">Access denied. Insufficient permissions.</div>
            </div>
        );
    }

    return (
        <div>
            <BackButton
                href={
                    isLockedToParentMerchant
                        ? `/organizations/${parentMerchantUuidFromUrl}`
                        : isLockedToParentIso
                          ? `/organizations/${parentIsoUuidFromUrl}`
                          : '/organizations/merchants'
                }
                tooltip={
                    isLockedToParentMerchant
                        ? 'Back to Parent Merchant'
                        : isLockedToParentIso
                          ? 'Back to ISO'
                          : 'Back to Organizations'
                }
            />
            <div className="flex items-center gap-4 mb-6">
                <h1 className="text-3xl font-bold">{pageTitle}</h1>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Merchant Information</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div>
                            <Label htmlFor="name">Merchant Name</Label>
                            <Input
                                id="name"
                                {...register('name')}
                                placeholder="Enter merchant name"
                            />
                            {errors.name && (
                                <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                            )}
                        </div>

                        {isParentMerchantLocked ? (
                            <div>
                                <Label>Parent Merchant</Label>
                                <p className="text-sm text-muted-foreground">
                                    {lockedParentMerchantName ?? 'Loading parent merchant...'}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <Label htmlFor="parent_organization_uuid">
                                    Parent Merchant (Optional)
                                </Label>
                                <div className="mt-2">
                                    <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                                        label="Search parent merchants"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={createMerchantMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_merchant: true,
                                                });
                                            return {
                                                items: response.organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_merchant')) {
                                                throw new Error(
                                                    `Expected merchant candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            setValue(
                                                'parent_organization_uuid',
                                                item.organization_uuid
                                            );
                                        }}
                                    />
                                </div>
                                {errors.parent_organization_uuid && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.parent_organization_uuid.message}
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <Label htmlFor="iso_organization_uuid">ISO of record (Optional)</Label>
                            {isParentIsoLocked ? (
                                <p className="text-sm text-muted-foreground mt-2">
                                    ISO of record is locked to:{' '}
                                    <span className="font-medium">
                                        {lockedParentIsoName ?? 'Loading ISO...'}
                                    </span>
                                </p>
                            ) : (
                                <div className="mt-2">
                                    <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                                        label="Search ISOs"
                                        placeholder="Type to search..."
                                        pageSize={25}
                                        disabled={createMerchantMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_iso: true,
                                                });
                                            return {
                                                items: response.organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_iso')) {
                                                throw new Error(
                                                    `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            setValue(
                                                'iso_organization_uuid',
                                                item.organization_uuid
                                            );
                                        }}
                                    />
                                </div>
                            )}
                            {isParentIsoLocked && (
                                <p className="text-sm text-muted-foreground">
                                    ISO of record is locked because you are creating a merchant for
                                    this specific ISO.
                                </p>
                            )}
                            {errors.iso_organization_uuid && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.iso_organization_uuid.message}
                                </p>
                            )}
                        </div>

                        {createMerchantMutation.error && (
                            <div className="text-red-600 text-sm">
                                Error: {String(createMerchantMutation.error)}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <Button
                                id="create-merchant-submit"
                                type="submit"
                                disabled={createMerchantMutation.isPending}
                            >
                                {createMerchantMutation.isPending
                                    ? 'Creating...'
                                    : 'Create Merchant'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CreateMerchantPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CreateMerchantContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
